import { formatLocaleDate, formatLocaleDateTime } from "@/utils/formatUtils";
import { Divider, Space, Typography } from "antd";
import { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { UserBase } from "../../modules/user/types";

interface ItemProps {
  createdAt?: string;
  createdBy?: UserBase;
  updatedAt?: string;
  verifiedAt?: string;
  verifiedBy?: UserBase;
  lastContractDate?: string;
}

export interface ItemMetaInfoEntry {
  key: string;
  label: ReactNode;
  value: ReactNode;
}

interface Props {
  item?: ItemProps;
  /** Screen-specific entries shown after the ones taken from `item` */
  entries?: ItemMetaInfoEntry[];
  className?: string;
  style?: CSSProperties;
}

const withUser = (date: string, user?: UserBase) => (user ? `${date} - ${user.name}` : date);

export const ItemMetaInfo = ({ item, entries = [], className, style }: Props) => {
  const { t } = useTranslation();

  const allEntries: ItemMetaInfoEntry[] = [
    item?.createdAt && {
      key: "createdAt",
      label: t("common.createdAt"),
      value: withUser(formatLocaleDateTime(item.createdAt), item.createdBy),
    },
    item?.updatedAt && {
      key: "updatedAt",
      label: t("common.updatedAt"),
      value: formatLocaleDateTime(item.updatedAt),
    },
    item?.verifiedAt && {
      key: "verifiedAt",
      label: t("common.verifiedAt"),
      value: withUser(formatLocaleDateTime(item.verifiedAt), item.verifiedBy),
    },
    item?.lastContractDate && {
      key: "lastContractDate",
      label: t("client.attrs.lastContractDate"),
      value: formatLocaleDate(item.lastContractDate),
    },
    ...entries,
  ].filter((entry) => !!entry);

  return allEntries.length > 0 ? (
    <Space
      wrap
      size={[0, 4]}
      separator={<Divider orientation="vertical" />}
      className={className}
      style={{ display: "flex", fontSize: 13, ...style }}
    >
      {allEntries.map((entry) => (
        <span key={entry.key}>
          <Typography.Text type="secondary">{entry.label}:</Typography.Text> {entry.value}
        </span>
      ))}
    </Space>
  ) : null;
};
